import { track } from '@doordash/lib-tracking'
import { get } from 'lodash'
import { RootState } from '../reducers'
import {
  DasherOnboardingFunnelCustomizedData,
  DasherOnboardingFunnelDeviceDetails,
  DasherOnboardingFunnelDxIdentifier,
  DasherOnboardingFunnelEventDetails,
  DasherOnboardingFunnelLocationDetails,
  DasherOnboardingFunnelSessionIdentifier,
} from '../typeModules/events'
import { getIsMobile, getMobileDevice } from '../utilities/userAgent'
import { cookieData } from './commonData'

const cookieSessionId = 'dd_session_id'
const cookieMarketId = 'dd_market_id'
const cookieSubmarketId = 'dd_submarket_id'
const cookieZipCode = 'dd_zip_code'
const cookieLocale = 'dd_locale'
const cookieLanguage = 'dd_language'
const eventName = 'dasher_onboarding_event'

/**
 * get DxIdentifiers related event properties
 * @param extraData The extraData passed in that would be in `dasherAdditionalDataJson` Json String
 */
function getDxIdentifier(
  state,
  customizedData: DasherOnboardingFunnelDxIdentifier = {},
  extraData: Record<string, string> = {}
): DasherOnboardingFunnelDxIdentifier {
  const uniqueLink = get(state, 'applicantUniqueLink') || customizedData.uniqueLink
  const dasherId = get(state, 'application.dasherId')
  const email = get(state, 'application.email')
  const onboardingStep = get(state, 'onboardingStep')
  const dasherAdditionalData = {
    ...extraData,
    email: email || undefined,
    onboardingStep: onboardingStep || undefined,
  }
  const dasherAdditionalDataJson = JSON.stringify(dasherAdditionalData)
  return {
    uniqueLink: uniqueLink || undefined,
    dasherId: dasherId || undefined,
    dasherAdditionalDataJson,
  }
}

/**
 * get Event related event properties:
 * Most Properties shoule be set in caller
 * @param eventDetails input `EventDetails` with most properties filledin
 * @param extraData The extraData passed in that would be in `eventAdditionalDataJson` Json String
 */
function getEventDetails(
  eventDetails: DasherOnboardingFunnelEventDetails,
  extraData: Record<string, string> = {}
): DasherOnboardingFunnelEventDetails {
  const eventAdditionalData = {
    ...extraData,
  }
  eventDetails.eventAdditionalDataJson = JSON.stringify(eventAdditionalData)
  return eventDetails
}

/**
 * get Location related event properties
 * @param cookies cookies start with `dd_` to get marketId, submarketId and zipcode
 * @param customizedData customized properties thats set by caller, would be a backup for properties getting from cookie
 */
function getLocationDetails(
  state,
  cookies: object,
  customizedData: DasherOnboardingFunnelLocationDetails = {}
): DasherOnboardingFunnelLocationDetails {
  const marketId = get(cookies, cookieMarketId) || customizedData.marketId
  const submarketId =
    get(cookies, cookieSubmarketId) || customizedData.submarketId
  const zipcode = get(cookies, cookieZipCode) || customizedData.zipcode
  const locale = get(cookies, cookieLanguage) || get(cookies, cookieLocale)
  const countryShortname = get(state, 'signupCountryShortname')

  return {
    marketId: marketId || undefined,
    submarketId: submarketId || undefined,
    zipcode: zipcode || undefined,
    locale: locale || undefined,
    countryShortname : countryShortname || undefined,
  }
}

/**
 * get SessionIdentifier related event properties
 * @param cookies cookies start with `dd_` to get sessionId
 */
function getSessionIdentifier(
  cookies: object
): DasherOnboardingFunnelSessionIdentifier {
  const sessionId = get(cookies, cookieSessionId)
  return {
    sessionId: sessionId || undefined,
  }
}
/**
 * get SessionIdentifier related event properties
 * @param ddCookies cookies start with dd_ that would be in `deviceAdditionalDataJson` Json String
 */
function getDeviceDetails(
  ddCookies: object
): DasherOnboardingFunnelDeviceDetails {
  const isMobile = getIsMobile()
  const devicePlatform = isMobile ? 'mobile' : 'desktop'
  const deviceType = isMobile ? getMobileDevice() : 'desktop'
  const userAgent = navigator.userAgent
  const hasTouchEvents = window.ontouchstart !== undefined
  const renderedBy = get(window, '__dd.sha')

  const cookieKeysForAdditionalJson = [
    'dd_session_id_2',
    'dd_device_id',
    'dd_device_id_2',
    'dd_login_id',
    'dd_zone_id',
    'dd_district_id',
  ]
  const cookiesForAdditionalJson = {}
  for (const cookieKey of cookieKeysForAdditionalJson) {
    cookiesForAdditionalJson[cookieKey] = get(ddCookies, cookieKey)
  }

  const deviceAdditionalData = {
    hasTouchEvents,
    renderedBy: renderedBy || undefined,
    innerWidth,
    innerHeight,
    ...cookiesForAdditionalJson,
  }
  const deviceAdditionalDataJson = JSON.stringify(deviceAdditionalData)

  return {
    devicePlatform,
    deviceType,
    userAgent: userAgent || undefined,
    deviceAdditionalDataJson,
  }
}

/**
 * A function for DasherOnboardingFunnel unified event schema that wraps
 * our Redux instrumentation tracking to append common event payload data
 * to all tracking events.
 *
 * @param eventDetails The eventDetails need to be attached
 * @param f A function that generates the event payload data
 *        f returns customizedDatas that include:
 *        1. customizedData that is set by caller to serve as a backup value for corresponding properties
 *        2. extracustomizedData thats for data that not suit in schema and can be put into `*AdditionalJson` String
 */
export const trackWithInfoV2 = (
  eventDetails: DasherOnboardingFunnelEventDetails,
  f: (args: RootState) => DasherOnboardingFunnelCustomizedData = () => ({})
) => {
  const cookie = document.cookie || ''
  return track(eventName, (state: RootState) => {
    const ddCookie = cookieData(cookie)
    const customizedData = f(state)

    const locationDetailsCustomizedData =
      customizedData && customizedData.locationDetailsCustomizedData
    const dxIdentifierCustomizedData =
      customizedData && customizedData.dxIdentifierCustomizedData
    const dxIdentifierCustomizedExtraData =
      customizedData &&
      customizedData.extraCustomizedData &&
      customizedData.extraCustomizedData.dxIdentifierCustomizedExtraData
    const eventDetailsCustomizedExtraData =
      customizedData &&
      customizedData.extraCustomizedData &&
      customizedData.extraCustomizedData.eventDetailsCustomizedExtraData

    return {
      ...getLocationDetails(state, ddCookie, locationDetailsCustomizedData),
      ...getSessionIdentifier(ddCookie),
      ...getDxIdentifier(state, dxIdentifierCustomizedData, dxIdentifierCustomizedExtraData),
      ...getEventDetails(eventDetails, eventDetailsCustomizedExtraData),
      ...getDeviceDetails(ddCookie),
    }
  })
}
